import { useState, useCallback, useMemo } from "react";
import { GoogleMap, MarkerF, InfoWindowF, PolylineF } from "@react-google-maps/api";
import { MapPin, CalendarDays, Map } from "lucide-react";
import { format, parseISO, isValid } from "date-fns";

const safeDate = (dateStr, fmt) => {
  if (!dateStr) return "—";
  try { const d = parseISO(dateStr); return isValid(d) ? format(d, fmt) : "—"; } catch { return "—"; }
};

const mapContainerStyle = { width: "100%", height: "400px", borderRadius: "12px" };
const defaultCenter = { lat: 48.2082, lng: 16.3738 }; // Vienna

// Geocode cache to avoid re-fetching
const geocodeCache = {};

const COUNTRY_COORDS = {
  "austria": { lat: 47.5162, lng: 14.5501 }, "france": { lat: 46.6034, lng: 1.8883 },
  "germany": { lat: 51.1657, lng: 10.4515 }, "italy": { lat: 41.8719, lng: 12.5674 },
  "spain": { lat: 40.4637, lng: -3.7492 }, "portugal": { lat: 39.3999, lng: -8.2245 },
  "united kingdom": { lat: 51.5074, lng: -0.1278 }, "uk": { lat: 51.5074, lng: -0.1278 },
  "united states": { lat: 39.8283, lng: -98.5795 }, "usa": { lat: 39.8283, lng: -98.5795 },
  "canada": { lat: 56.1304, lng: -106.3468 }, "japan": { lat: 36.2048, lng: 138.2529 },
  "australia": { lat: -25.2744, lng: 133.7751 }, "brazil": { lat: -14.235, lng: -51.9253 },
  "india": { lat: 20.5937, lng: 78.9629 }, "china": { lat: 35.8617, lng: 104.1954 },
  "thailand": { lat: 15.87, lng: 100.9925 }, "greece": { lat: 39.0742, lng: 21.8243 },
  "turkey": { lat: 38.9637, lng: 35.2433 }, "egypt": { lat: 26.8206, lng: 30.8025 },
  "mexico": { lat: 23.6345, lng: -102.5528 }, "switzerland": { lat: 46.8182, lng: 8.2275 },
  "netherlands": { lat: 52.1326, lng: 5.2913 }, "belgium": { lat: 50.5039, lng: 4.4699 },
  "norway": { lat: 60.472, lng: 8.4689 }, "sweden": { lat: 60.1282, lng: 18.6435 },
  "denmark": { lat: 56.2639, lng: 9.5018 }, "finland": { lat: 61.9241, lng: 25.7482 },
  "poland": { lat: 51.9194, lng: 19.1451 }, "czech republic": { lat: 49.8175, lng: 15.473 },
  "czechia": { lat: 49.8175, lng: 15.473 }, "hungary": { lat: 47.1625, lng: 19.5033 },
  "croatia": { lat: 45.1, lng: 15.2 }, "ireland": { lat: 53.1424, lng: -7.6921 },
  "iceland": { lat: 64.9631, lng: -19.0208 }, "south korea": { lat: 35.9078, lng: 127.7669 },
  "new zealand": { lat: -40.9006, lng: 174.886 }, "argentina": { lat: -38.4161, lng: -63.6167 },
  "colombia": { lat: 4.5709, lng: -74.2973 }, "peru": { lat: -9.19, lng: -75.0152 },
  "south africa": { lat: -30.5595, lng: 22.9375 }, "morocco": { lat: 31.7917, lng: -7.0926 },
  "vietnam": { lat: 14.0583, lng: 108.2772 }, "singapore": { lat: 1.3521, lng: 103.8198 },
  "malaysia": { lat: 4.2105, lng: 101.9758 }, "indonesia": { lat: -0.7893, lng: 113.9213 },
  "philippines": { lat: 12.8797, lng: 121.774 }, "russia": { lat: 61.524, lng: 105.3188 },
  "romania": { lat: 45.9432, lng: 24.9668 }, "bulgaria": { lat: 42.7339, lng: 25.4858 },
  "serbia": { lat: 44.0165, lng: 21.0059 }, "slovakia": { lat: 48.669, lng: 19.699 },
  "slovenia": { lat: 46.1512, lng: 14.9955 }, "portugal": { lat: 39.3999, lng: -8.2245 },
  "chile": { lat: -35.6751, lng: -71.543 }, "cuba": { lat: 21.5218, lng: -77.7812 },
  "costa rica": { lat: 9.7489, lng: -83.7534 }, "panama": { lat: 8.538, lng: -80.7821 },
  "kenya": { lat: -0.0236, lng: 37.9062 }, "tanzania": { lat: -6.369, lng: 34.8888 },
  "united arab emirates": { lat: 23.4241, lng: 53.8478 }, "saudi arabia": { lat: 23.8859, lng: 45.0792 },
  "israel": { lat: 31.0461, lng: 34.8516 }, "jordan": { lat: 30.5852, lng: 36.2384 },
  "nepal": { lat: 28.3949, lng: 84.124 }, "sri lanka": { lat: 7.8731, lng: 80.7718 },
  "maldives": { lat: 3.2028, lng: 73.2207 }, "cambodia": { lat: 12.5657, lng: 104.991 },
};

function getCountryCoords(countryName) {
  if (!countryName) return null;
  return COUNTRY_COORDS[countryName.toLowerCase().trim()] || null;
}

const COLORS = [
  "#38bdf8", "#f472b6", "#a78bfa", "#34d399", "#fbbf24",
  "#fb923c", "#f87171", "#2dd4bf", "#818cf8", "#e879f9",
];

export const TripMap = ({ trips, isGoogleLoaded }) => {
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [showMap, setShowMap] = useState(false);

  const markers = useMemo(() => {
    return trips
      .map((trip, idx) => {
        const coords = getCountryCoords(trip.destination_country);
        if (!coords) return null;
        return { ...trip, position: coords, color: COLORS[idx % COLORS.length] };
      })
      .filter(Boolean);
  }, [trips]);

  // Route path: markers sorted by start_date
  const routePath = useMemo(() => {
    return [...markers]
      .sort((a, b) => (a.start_date || "").localeCompare(b.start_date || ""))
      .map((m) => m.position);
  }, [markers]);

  const center = useMemo(() => {
    if (markers.length === 0) return defaultCenter;
    const avgLat = markers.reduce((s, m) => s + m.position.lat, 0) / markers.length;
    const avgLng = markers.reduce((s, m) => s + m.position.lng, 0) / markers.length;
    return { lat: avgLat, lng: avgLng };
  }, [markers]);

  if (!isGoogleLoaded) return null;

  return (
    <div className="mb-6" data-testid="trip-map-section">
      <button
        onClick={() => setShowMap(!showMap)}
        className="flex items-center gap-2 mb-3 text-sky-400 hover:text-sky-300 transition-colors"
        data-testid="toggle-trip-map"
      >
        <Map className="w-5 h-5" />
        <span className="font-medium">{showMap ? "Hide Map" : "Show Trip Map"}</span>
      </button>

      {showMap && (
        <div className="rounded-xl overflow-hidden border border-white/10">
          <GoogleMap
            mapContainerStyle={mapContainerStyle}
            center={center}
            zoom={markers.length === 1 ? 5 : 2}
            options={{
              styles: [
                { elementType: "geometry", stylers: [{ color: "#1a1a2e" }] },
                { elementType: "labels.text.stroke", stylers: [{ color: "#1a1a2e" }] },
                { elementType: "labels.text.fill", stylers: [{ color: "#8a8a9a" }] },
                { featureType: "water", elementType: "geometry", stylers: [{ color: "#0e1529" }] },
                { featureType: "road", elementType: "geometry", stylers: [{ color: "#2a2a3e" }] },
                { featureType: "poi", stylers: [{ visibility: "off" }] },
              ],
              disableDefaultUI: true,
              zoomControl: true,
            }}
          >
            {markers.map((m) => (
              <MarkerF
                key={m.trip_id}
                position={m.position}
                onClick={() => setSelectedTrip(m)}
                icon={{
                  path: "M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z",
                  fillColor: m.color,
                  fillOpacity: 1,
                  strokeColor: "#ffffff",
                  strokeWeight: 1.5,
                  scale: 1.5,
                  anchor: { x: 12, y: 24 },
                }}
              />
            ))}

            {/* Route line connecting trips by date order */}
            {routePath.length >= 2 && (
              <PolylineF
                path={routePath}
                options={{
                  strokeColor: "#38bdf8",
                  strokeOpacity: 0.6,
                  strokeWeight: 2,
                  geodesic: true,
                  icons: [{
                    icon: { path: 2, scale: 3, strokeColor: "#38bdf8" },
                    offset: "50%",
                    repeat: "100px",
                  }],
                }}
              />
            )}

            {selectedTrip && (
              <InfoWindowF
                position={selectedTrip.position}
                onCloseClick={() => setSelectedTrip(null)}
              >
                <div className="p-1 min-w-[160px]" style={{ color: "#1a1a2e" }}>
                  <h4 className="font-bold text-sm">{selectedTrip.name}</h4>
                  <div className="flex items-center gap-1 mt-1 text-xs text-gray-600">
                    <MapPin className="w-3 h-3" />
                    {selectedTrip.destination_country}
                  </div>
                  <div className="flex items-center gap-1 mt-1 text-xs text-gray-500">
                    <CalendarDays className="w-3 h-3" />
                    {safeDate(selectedTrip.start_date, "MMM d")} - {safeDate(selectedTrip.end_date, "MMM d, yyyy")}
                  </div>
                </div>
              </InfoWindowF>
            )}
          </GoogleMap>
        </div>
      )}
    </div>
  );
};
